import React, { useContext, useMemo } from 'react'
import { rem } from 'polished'
import styled from 'styled-components'
import Heading from 'LuxKit/Typography/Heading'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Group from 'components/utils/Group'
import TextButton from 'LuxKit/Button/TextButton'
import FormatCurrency from 'components/Common/FormatCurrency/FormatCurrency'
import BodyText from 'LuxKit/Typography/BodyText'
import { pluralizeToString } from 'lib/string/pluralize'
import Image from 'components/Common/Image/Image'
import CarHireCancellationPolicyText from '../CarHireCancellationPolicyText'
import ModalContext from 'contexts/ModalContext'
import noop from 'lib/function/noop'
import { mediaQueryUp } from 'components/utils/breakpoint'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import StatusLabel from 'LuxKit/Label/StatusLabel'
import StructureLabel from 'LuxKit/Label/StructureLabel'
import CarHireInclusionsAndDetailsModal from './CarHireInclusionsAndDetailsModal'
import CarHireOfferTileLocation from './CarHireOfferTileLocation'
import CarHireOfferTileVehicleDetailsCondensed from './CarHireOfferTileVehicleDetailsCondensed'
import CarHirePriceDetailsModal from './CarHirePriceDetailsModal'
import PriceRowTaxesAndFees from 'LuxKit/PricePoints/PriceRowTaxesAndFees'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowCaption from 'LuxKit/PricePoints/PriceRowCaption'
import PriceRowPriceDetails from 'LuxKit/PricePoints/PriceRowPriceDetails'
import CarHireOfferTileInclusions from './CarHireOfferTileInclusions'
import { isCarHireInsuranceAvailable } from 'selectors/carHireSelectors'
import CarHireInsuranceText from '../CarHireInsuranceText'
import Tooltip from 'LuxKit/Tooltip'
import LineCheckCircleIcon from 'LuxKit/Icons/line/LineCheckCircleIcon'
import Pill from 'LuxKit/Pill'
import { connect } from 'react-redux'
import { isPayFullyOnArrivalAvailable, isPayPartiallyAvailable } from 'lib/carHire/carHireUtils'

const Tile = styled.div`
  padding: ${rem(20)};
  width: 100%;
  display: grid;
  background-color: ${props => props.theme.palette.neutral.default.eight};
  border: 1px solid ${props => props.theme.palette.neutral.default.five};
  gap: ${rem(8)};
  grid-template-columns: 1fr;
`

const TileContent = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${rem(12)};

  ${mediaQueryUp.desktop} {
    gap: ${rem(16)};
  }
`

const CarImage = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  filter: brightness(1.25);
  height: 100%;
`

interface Props {
  offer: App.CarHireOffer;
  className?: string;
  onReserve?: (offer: App.CarHireOffer) => void;
  priceFormat?: 'per-day' | 'total';
  pickUpDate: string;
  dropOffDate: string;
  pickUpTime: string;
  dropOffTime: string;
  isCarHireInsuranceAvailable: boolean;
}

function CarHireOfferTileCondensed(props: Props) {
  const {
    offer,
    className,
    onReserve = noop,
    priceFormat = 'per-day',
    pickUpDate,
    dropOffDate,
    pickUpTime,
    dropOffTime,
    isCarHireInsuranceAvailable,
  } = props

  const isPayOnArrivalAvailable = isPayFullyOnArrivalAvailable(offer)
  const hasPayAtPickupAmount = isPayOnArrivalAvailable || isPayPartiallyAvailable(offer)

  const showModal = useContext(ModalContext)
  const onViewInclusions = () => {
    showModal(
      <CarHireInclusionsAndDetailsModal
        offer={offer}
        pickUpDate={pickUpDate}
        dropOffDate={dropOffDate}
        pickUpTime={pickUpTime}
        dropOffTime={dropOffTime}
        showInclusions
        title="All inclusions & details"
      />)
  }

  const onViewPriceDetails = () => {
    showModal(<CarHirePriceDetailsModal offer={offer} pickUpDate={pickUpDate} onReserve={onReserve} />)
  }

  const desktopImage = useMemo(() => ({
    ...offer.vehicle.image,
    // modify the image URL to utilise car trawlers image options for a better image
    url: `${offer.vehicle.image.url}?w=216&dpr=2`,
  }), [offer.vehicle.image])

  const mobileImage = useMemo(() => ({
    ...offer.vehicle.image,
    // modify the image URL to utilise car trawlers image options for a better image
    // 'secondary' faces left, 'primary' faces right
    url: `${offer.vehicle.image.url?.replace('primary', 'secondary')}?w=108&dpr=2`,
  }), [offer.vehicle.image])

  return <Tile className={className}>
    {/* desktop */}
    <CSSBreakpoint min="desktop">
      <Group direction="vertical" gap={8}>
        <Group direction="horizontal" horizontalAlign="space-between">
          <Group direction="horizontal" fullWidth horizontalAlign="end">
            <Image width={80} image={offer.vendor.image} />
          </Group>
        </Group>
        <CarImage>
          <Image width={216} height={121.5} image={desktopImage} />
        </CarImage>
        <LabelGroup>
          <StructureLabel variant="standard">{offer.vehicle.type}</StructureLabel>
          {offer.guarenteedModel && <StatusLabel variant="confirmation">Guaranteed model</StatusLabel>}
        </LabelGroup>
      </Group>
    </CSSBreakpoint>
    {/* mobile */}
    <CSSBreakpoint max="tablet">
      <Group direction="horizontal" horizontalAlign="space-between">
        <LabelGroup>
          <StructureLabel variant="standard">{offer.vehicle.type}</StructureLabel>
          {offer.guarenteedModel && <StatusLabel variant="confirmation">Guaranteed model</StatusLabel>}
        </LabelGroup>
        <Group direction="horizontal" gap={8} verticalAlign="center">
          <Image width={64} image={offer.vendor.image} />
        </Group>
      </Group>
    </CSSBreakpoint>

    <TileContent>
      <VerticalSpacer gap={12}>
        <VerticalSpacer gap={8}>
          <div>
            <Heading as="span" variant="heading3">{offer.vehicle.model}</Heading>{' '}
            {!offer.guarenteedModel && <BodyText wrap="no-wrap" as="span" variant="medium" colour="neutral-three">or similar</BodyText>}
          </div>
          <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center" gap={8}>
            <VerticalSpacer gap={4}>
              <CarHireOfferTileVehicleDetailsCondensed offer={offer} showMileage={false} />
              <CarHireOfferTileLocation offer={offer} />
            </VerticalSpacer>
            <CSSBreakpoint max="tablet">
              <CarImage>
                <Image width={108} image={mobileImage} />
              </CarImage>
            </CSSBreakpoint>
          </Group>
        </VerticalSpacer>
        <CarHireOfferTileInclusions offer={offer} onViewInclusions={onViewInclusions} />
      </VerticalSpacer>

      <VerticalSpacer gap={0}>
        {offer.cancellationPolicy && <CarHireCancellationPolicyText policy={offer.cancellationPolicy} />}
        {isCarHireInsuranceAvailable &&
          <CarHireInsuranceText
            offer={offer}
            isEnhancedProtectionSelected={false}
            pickUpDate={pickUpDate || ''}
            dropOffDate={dropOffDate || ''}
          />
        }
      </VerticalSpacer>

      <Group
        direction="horizontal"
        horizontalAlign="space-between"
        verticalAlign="end"
        gap={8}
      >
        <VerticalSpacer gap={8}>
          {isPayOnArrivalAvailable && <Tooltip description="Reserve a car today with $0 and pay the total amount at pick-up.">
            <Pill kind="tertiary" variant="primary" startIcon={<LineCheckCircleIcon />}>Pay at pick-up</Pill>
          </Tooltip>}
          <div>
            {priceFormat === 'per-day' && <>
              <PriceRowPrice
                size="L"
                price={offer.pricePerDay}
                saleUnit="day"
              />
              {offer.value > offer.price && <BodyText variant="medium" colour="neutral-two" strikeThrough>Was <FormatCurrency value={offer.valuePerDay} /></BodyText>}
              {offer.duration > 1 && <PriceRowCaption>
                Total <FormatCurrency value={offer.price} /> for {pluralizeToString('day', offer.duration)}
              </PriceRowCaption>}
            </>}
            {priceFormat === 'total' && <>
              {offer.duration > 1 && <PriceRowPriceCaption>
                For {pluralizeToString('day', offer.duration)}
              </PriceRowPriceCaption>}
              <PriceRowPrice
                size="L"
                price={offer.price}
                saleUnit="total"
              />
              {offer.duration > 1 && <PriceRowCaption>
                <FormatCurrency value={offer.pricePerDay} />/day
              </PriceRowCaption>}
            </>}
            <PriceRowTaxesAndFees />
            {hasPayAtPickupAmount && <PriceRowPriceDetails size="M" onClick={onViewPriceDetails} />}
          </div>
        </VerticalSpacer>
        <TextButton kind="primary" onClick={() => onReserve(offer)}>
          Book now
        </TextButton>
      </Group>
    </TileContent>
  </Tile>
}

function mapStateToProps(state: App.State) {
  return {
    isCarHireInsuranceAvailable: isCarHireInsuranceAvailable(state),
  }
}

export default connect(mapStateToProps)(React.memo(CarHireOfferTileCondensed))
